// BookingCheckout.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useBooking } from '../context/BookingContext';
import api from '../services/api';
import { useNavigate, Link } from 'react-router-dom';




export default function BookingCheckout() {
  const { bookingData, setBookingData } = useBooking();
  const paypalRef = useRef();
  const navigate = useNavigate();

  const [paid, setPaid] = useState(false);
  const [error, setError] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [paypalLoaded, setPaypalLoaded] = useState(false);

  // 🧠 Persist booking info in localStorage
  useEffect(() => {
    if (bookingData && Object.keys(bookingData).length > 0) {
      localStorage.setItem('bookingData', JSON.stringify(bookingData));
    }
  }, [bookingData]);

  // 🧠 Restore if page reloaded
  useEffect(() => {
    const saved = localStorage.getItem('bookingData');
    if (saved && (!bookingData || Object.keys(bookingData).length === 0)) {
      setBookingData(JSON.parse(saved));
    }
  }, [setBookingData]);

  const { checkInDate, checkOutDate, guests, selectedRoom, optionalServices, guestInfo } = bookingData || {};

  const totalNights =
    checkInDate && checkOutDate
      ? (new Date(checkOutDate) - new Date(checkInDate)) / (1000 * 60 * 60 * 24)
      : 0;

  const roomCost = selectedRoom?.price * (1 - (4 - (guests || 1)) * 0.08) || 0;
  const servicesCost =
    (optionalServices?.breakfast ? 10 : 0) +
    (optionalServices?.earlyCheckIn ? 15 : 0) +
    (optionalServices?.pets ? 20 : 0);

  const total = totalNights * roomCost + servicesCost;

  const generateBookingId = () => 'BK-' + Math.random().toString(36).slice(2, 8).toUpperCase();

  // 🧾 Validation before showing PayPal
  const canPay =
    acceptedTerms &&
    guestInfo?.fullName &&
    guestInfo?.email &&
    guestInfo?.phone &&
    guestInfo?.address &&
    selectedRoom &&
    total > 0;

  // 💳 PayPal integration
  useEffect(() => {
    if (!canPay || paid) return;

    const renderPayPalButtons = () => {
      if (!paypalRef.current) return;
      paypalRef.current.innerHTML = '';

      window.paypal
        .Buttons({
          createOrder: async () => {
            const res = await api.post('/paypal/create-order', { total });
            return res.data.id;
          },
          onApprove: async (data) => {
            try {
              await api.post('/paypal/capture-order', { orderID: data.orderID });

              const payload = {
                checkInDate,
                checkOutDate,
                guests,
                roomId: selectedRoom._id,
                services: optionalServices,
                guest: guestInfo,
                paymentMethod: 'Card/Paypal',
                bookingId: generateBookingId(),
              };

              const res = await api.post('/bookings', payload);
              setPaid(true);
              setBookingData({ ...bookingData, bookingId: res.data.bookingId });

              localStorage.removeItem('bookingData');
              navigate('/booking/confirmation');
            } catch (err) {
              console.error('Booking save error:', err.response?.data || err.message);
              setError('Payment was processed, but booking could not be completed. Please contact support.');
            }
          },
          onError: (err) => {
            console.error(err);
            setError('PayPal checkout error.');
          },
        })
        .render(paypalRef.current);

      setPaypalLoaded(true);
    };

    // ✅ Load PayPal dynamically from backend config
    async function loadPayPalScript() {
      try {
        const { data } = await api.get('/paypal/config');
        const { clientId, currency } = data;

        // If script already loaded with same client ID, re-render
        if (window.paypal && window.paypal.ClientID === clientId) {
          renderPayPalButtons();
          return;
        }

        // Otherwise, load script dynamically
        const script = document.createElement('script');
        script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=${currency}`;
        script.addEventListener('load', renderPayPalButtons);
        document.body.appendChild(script);
      } catch (err) {
        console.error('Failed to load PayPal config:', err);
        setError('Unable to load PayPal. Please try again later.');
      }
    }

    loadPayPalScript();
  }, [
    canPay,
    total,
    paid,
    checkInDate,
    checkOutDate,
    guests,
    selectedRoom,
    optionalServices,
    guestInfo,
    bookingData,
    setBookingData,
    navigate,
  ]);

  return (
    <div className="checkout-page" style={{ padding: '2rem', maxWidth: '600px', margin: 'auto' }}>
      <h2 className="text-2xl font-semibold mb-4">Checkout</h2>

      <div className="summary bg-gray-50 p-4 rounded-lg shadow-sm mb-4">
        <h3 className="text-lg font-medium">Booking Summary</h3>
        <p><strong>Room:</strong> {selectedRoom?.name || 'N/A'}</p>
        <p><strong>Check-in:</strong> {checkInDate}</p>
        <p><strong>Check-out:</strong> {checkOutDate}</p>
        <p><strong>Guests:</strong> {guests}</p>
        <p><strong>Total Nights:</strong> {totalNights}</p>
        <p><strong>Services:</strong> {Object.entries(optionalServices || {})
          .filter(([_, v]) => v)
          .map(([k]) => k)
          .join(', ') || 'None'}
        </p>
        <h3 className="text-xl font-bold mt-2">Total: ${total.toFixed(2)} MXN</h3>
      </div>

      <div className="terms bg-white p-4 border rounded-lg mb-3">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
          />
          <span>
            I have read and accept the{' '}
            <Link to="/terms" target="_blank" className="text-blue-600 underline">
              Terms & Conditions
            </Link>
            .
          </span>
        </label>
      </div>

      <div className="important-info text-sm bg-yellow-50 border-l-4 border-yellow-500 p-3 rounded mb-4">
        <h4 className="font-semibold">Importante</h4>
        <ul className="list-disc ml-5 mt-2">
<li>Todas las reservaciones son <strong>no reembolsables</strong>. Por favor, revisa tus fechas cuidadosamente.</li>
<li>El check-in es a las 3:00 PM y el check-out a las 12:00 PM.</li>
<li>Se permiten mascotas únicamente si fueron seleccionadas y confirmadas.</li>
<li>Los huéspedes son responsables por cualquier daño ocasionado durante su estancia.</li>
<li>Se requerirá una identificación válida al momento del check-in (INE, pasaporte, etc.).</li>
<li>Casa Gloria se encuentra en <strong>Querétaro, México</strong>. Todos los precios incluyen IVA.</li>

        </ul>
      </div>

      {error && <p style={{ color: 'red', marginBottom: '10px' }}>{error}</p>}

      {!acceptedTerms && (
        <p style={{ color: 'gray', fontSize: '0.9rem' }}>
          ✅ Por favor acepta los términos y condiciones para continuar.
        </p>
      )}

      {paid ? (
        <p style={{ color: 'green', fontWeight: 'bold' }}>✅ Booking confirmed! Redirecting...</p>
      ) : (
        <div>
          <div
            ref={paypalRef}
            style={{
              marginTop: '1rem',
              opacity: canPay ? 1 : 0.5,
              pointerEvents: canPay ? 'auto' : 'none',
              transition: 'opacity 0.3s ease',
            }}
          />
          {!paypalLoaded && canPay && <p>Loading PayPal...</p>}
        </div>
      )}
    </div>
  );
}
